import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, IconButton, Tooltip, Snackbar, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Switch, FormControlLabel, CircularProgress
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LockResetIcon from '@mui/icons-material/LockReset';
import LinkIcon from '@mui/icons-material/Link';
import SearchIcon from '@mui/icons-material/Search';
import axios from "../../utils/axios";
export default function Users() {
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState("");
  const [snack, setSnack] = useState({ open: false, text: "", severity: "success" });
  const [editingUser, setEditingUser] = useState(null);
  const [resetUser, setResetUser] = useState(null);
  const [viewLinksUser, setViewLinksUser] = useState(null);
  const [userLinks, setUserLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem("token");

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/teolucpro/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(res.data.users || []);
    } catch {
      setSnack({ open: true, text: "Lỗi tải user", severity: "error" });
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const filteredUsers = users.filter(
    (u) =>
      (u.username && u.username.toLowerCase().includes(filter.toLowerCase())) ||
      (u.name && u.name.toLowerCase().includes(filter.toLowerCase()))
  );

  const handleSaveEdit = () => {
    setLoading(true);
    axios.patch(`/teolucpro/users/${editingUser.id}`, editingUser, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(() => {
        setSnack({ open: true, text: "Đã lưu thông tin user!", severity: "success" });
        fetchUsers();
        setEditingUser(null);
      })
      .catch(() => setSnack({ open: true, text: "Lỗi cập nhật!", severity: "error" }))
      .finally(() => setLoading(false));
  };

  const handleResetPass = () => {
    setLoading(true);
    axios.post(`/teolucpro/users/${resetUser.id}/reset-pass`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(() => setSnack({ open: true, text: "Đã reset mật khẩu!", severity: "success" }))
      .catch(() => setSnack({ open: true, text: "Lỗi reset!", severity: "error" }))
      .finally(() => { setLoading(false); setResetUser(null); });
  };

  const handleViewLinksUser = async (user) => {
    setLoading(true);
    setUserLinks([]);
    try {
      const res = await axios.get(`/teolucpro/users/${user.id}/links`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserLinks(res.data.links || []);
    } catch {
      setUserLinks([]);
    } finally {
      setViewLinksUser(user);
      setLoading(false);
    }
  };

  const handleToggleActive = async (user) => {
    setLoading(true);
    try {
      await axios.patch(`/teolucpro/users/${user.id}`, {
        is_active: !user.is_active
      }, { headers: { Authorization: `Bearer ${token}` } });
      setSnack({ open: true, text: `Đã ${user.is_active ? "khóa" : "mở"} tài khoản "${user.username}"`, severity: "success" });
      fetchUsers();
    } catch {
      setSnack({ open: true, text: "Lỗi cập nhật trạng thái!", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = (user) => {
    if (!window.confirm(`Xác nhận xoá user "${user.username}"?`)) return;
    setLoading(true);
    axios.delete(`/teolucpro/users/${user.id}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(() => {
        setSnack({ open: true, text: "Đã xoá user!", severity: "success" });
        fetchUsers();
      })
      .catch(() => setSnack({ open: true, text: "Lỗi xoá user!", severity: "error" }))
      .finally(() => setLoading(false));
  };

  const columns = [
    { field: "id", headerName: "ID", width: 70 },
    { field: "username", headerName: "Username", width: 130 },
    { field: "name", headerName: "Tên", width: 180 },
    { field: "phone", headerName: "SĐT", width: 120 },
    {
      field: "role", headerName: "Vai trò", width: 100, renderCell: (p) =>
        <span style={{ color: p.value === "admin" ? "#eab308" : "#06b6d4", fontWeight: 700 }}>{p.value}</span>
    },
    {
      field: "is_active", headerName: "Trạng thái", width: 130, renderCell: (p) =>
        <FormControlLabel
          control={
            <Switch
              checked={p.value}
              onChange={() => handleToggleActive(p.row)}
              color="success"
              size="small"
            />
          }
          label={p.value ? "Hoạt động" : "Khoá"}
          labelPlacement="end"
        />
    },
    { field: "created_at", headerName: "Ngày tạo", width: 120 },
    {
      field: "actions", headerName: "Thao tác", width: 200, renderCell: (p) => (
        <Box>
          <Tooltip title="Sửa user"><IconButton color="primary" onClick={() => setEditingUser({ ...p.row })}><EditIcon /></IconButton></Tooltip>
          <Tooltip title="Reset mật khẩu"><IconButton color="info" onClick={() => setResetUser(p.row)}><LockResetIcon /></IconButton></Tooltip>
          <Tooltip title="Xem link user tạo"><IconButton color="secondary" onClick={() => handleViewLinksUser(p.row)}><LinkIcon /></IconButton></Tooltip>
          <Tooltip title="Xoá"><IconButton color="error" onClick={() => handleDeleteUser(p.row)}><DeleteIcon /></IconButton></Tooltip>
        </Box>
      )
    }
  ];

  return (
    <Box>
      <Typography variant="h5" fontWeight={800} sx={{ mb: 3 }}>
        Quản lý user
      </Typography>

      <Box sx={{ display: "flex", mb: 2, gap: 2 }}>
        <TextField
          size="small"
          variant="outlined"
          placeholder="Tìm kiếm username, tên..."
          value={filter}
          onChange={e => setFilter(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: "#6366f1" }} />
          }}
          sx={{ maxWidth: 340 }}
        />
        {loading && <CircularProgress size={28} />}
      </Box>

      <Box sx={{ height: 500, bgcolor: "#fff", borderRadius: 4, p: 2, boxShadow: "0 2px 12px #6366f122" }}>
        <DataGrid
          rows={filteredUsers}
          columns={columns}
          checkboxSelection
          disableRowSelectionOnClick
          pageSize={8}
          loading={loading}
        />
      </Box>

      <Dialog open={!!editingUser} onClose={() => setEditingUser(null)}>
        <DialogTitle>Sửa thông tin user</DialogTitle>
        {editingUser && (
          <DialogContent sx={{ minWidth: 340 }}>
            <TextField margin="normal" label="Tên" fullWidth value={editingUser.name || ""} onChange={e => setEditingUser({ ...editingUser, name: e.target.value })} />
            <TextField margin="normal" label="SĐT" fullWidth value={editingUser.phone || ""} onChange={e => setEditingUser({ ...editingUser, phone: e.target.value })} />
            <TextField
              margin="normal"
              label="Vai trò"
              select
              SelectProps={{ native: true }}
              fullWidth
              value={editingUser.role}
              onChange={e => setEditingUser({ ...editingUser, role: e.target.value })}
            >
              <option value="admin">admin</option>
              <option value="user">user</option>
            </TextField>
            <FormControlLabel
              control={<Switch checked={editingUser.is_active} onChange={e => setEditingUser({ ...editingUser, is_active: e.target.checked })} color="success" />}
              label={editingUser.is_active ? "Hoạt động" : "Khoá"}
              sx={{ mt: 2 }}
            />
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setEditingUser(null)}>Hủy</Button>
          <Button onClick={handleSaveEdit} variant="contained">Lưu</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!resetUser} onClose={() => setResetUser(null)}>
        <DialogTitle>Reset mật khẩu</DialogTitle>
        <DialogContent>Bạn chắc chắn muốn reset mật khẩu user <b>{resetUser?.username}</b>?</DialogContent>
        <DialogActions>
          <Button onClick={() => setResetUser(null)}>Hủy</Button>
          <Button onClick={handleResetPass} variant="contained" color="info">Reset</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!viewLinksUser} onClose={() => setViewLinksUser(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          Danh sách link của user <b>{viewLinksUser?.username}</b>
        </DialogTitle>
        <DialogContent>
          {loading ? <CircularProgress sx={{ my: 2 }} /> : (
            userLinks.length === 0 ? (
              <Typography>Chưa có link nào.</Typography>
            ) : (
              <Box sx={{ my: 2 }}>
                <DataGrid
                  rows={userLinks}
                  columns={[
                    { field: "id", headerName: "ID", width: 60 },
                    { field: "short_code", headerName: "Short", width: 100 },
                    { field: "original_url", headerName: "URL gốc", width: 280 },
                    { field: "clicks", headerName: "Lượt click", width: 120 },
                    { field: "created_at", headerName: "Ngày tạo", width: 120 }
                  ]}
                  autoHeight
                  hideFooterPagination
                />
              </Box>
            )
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewLinksUser(null)}>Đóng</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={2200}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        onClose={() => setSnack({ ...snack, open: false })}
      >
        <Alert severity={snack.severity} sx={{ width: "100%" }}>
          {snack.text}
        </Alert>
      </Snackbar>
    </Box>
  );
}
